import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { LIBELLES_TYPE_PURGE, jourDeTunis, quandPurge, type EtatConservation } from '@rh/shared';
import { appel } from '../api';
import { Chargement, Erreur, Vide } from '../composants/Etat';
import { Prolonger } from '../composants/Prolonger';
import { jourLisible, nomComplet } from '../format';

/** Écran Conservation (docs/UI.md) : préavis d'anonymisation et journal des purges. */
export function Conservation() {
  const [etat, setEtat] = useState<EtatConservation | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  const charger = useCallback(() => {
    appel<EtatConservation>('/conservation')
      .then(setEtat)
      .catch((e: Error) => setErreur(e.message));
  }, []);

  useEffect(charger, [charger]);

  if (erreur) return <Erreur message={erreur} />;
  if (!etat) return <Chargement quoi="de la conservation" />;
  const aujourdhui = jourDeTunis();

  return (
    <section className="conservation">
      <div className="entete-annonce">
        <h2>Conservation</h2>
      </div>
      <p className="mention">
        Un candidat non embauché est anonymisé {etat.mois} mois après sa dernière candidature ou son
        dernier contact effectif (candidat joint, entretien). Le préavis dure {etat.preavisJours} jours et
        la purge passe chaque nuit à 2 h 30. Les salariés ne sont jamais concernés.
      </p>

      <h3>En préavis ({etat.enPreavis.length})</h3>
      {etat.enPreavis.length === 0 ? (
        <Vide enfants="Aucune fiche n’arrive à échéance dans le délai de préavis." />
      ) : (
        <ul className="liste-conservation">
          {etat.enPreavis.map((fiche) => (
            <li key={fiche.personneId} className="carte-conservation">
              <div className="corps">
                {fiche.candidatureId ? (
                  <Link to={`/candidatures/${fiche.candidatureId}`}>
                    <strong>{nomComplet(fiche)}</strong>
                  </Link>
                ) : (
                  <strong>{nomComplet(fiche)}</strong>
                )}
                <p className="mention">
                  {fiche.annonceIntitule ?? 'Sans annonce'}
                  {fiche.nbCandidatures > 1 && ` · ${fiche.nbCandidatures} candidatures`} · dernière activité le{' '}
                  {new Date(fiche.derniereActivite).toLocaleDateString('fr-FR')}
                  {fiche.prolongeeLe && ' · déjà prolongée'}
                </p>
                <p className="alerte">
                  Anonymisation le {jourLisible(fiche.purgePrevueLe)}, {quandPurge(fiche.purgePrevueLe, aujourdhui)}
                </p>
              </div>
              <Prolonger personneId={fiche.personneId} onProlongee={charger} />
            </li>
          ))}
        </ul>
      )}

      <h3>Journal des purges</h3>
      {etat.fichiersEnAttente > 0 && (
        <p className="signal signal-alerte">
          {etat.fichiersEnAttente} fichier(s) de fiches purgées restent à effacer du disque : le worker
          réessaie chaque nuit.
        </p>
      )}
      {etat.purges.length === 0 ? (
        <Vide enfants="Aucune purge pour l’instant." />
      ) : (
        <div className="defilement">
          <table className="journal-purges">
            <thead>
              <tr>
                <th>Date</th>
                <th>Opération</th>
                <th className="nombre">Fiches</th>
                <th className="nombre">Fichiers</th>
                <th>Par</th>
                <th>Motif</th>
              </tr>
            </thead>
            <tbody>
              {etat.purges.map((purge) => (
                <tr key={purge.id}>
                  <td>{new Date(purge.executeeLe).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}</td>
                  <td>{LIBELLES_TYPE_PURGE[purge.type]}</td>
                  <td className="nombre">{purge.nbFiches}</td>
                  <td className="nombre">{purge.nbFichiers}</td>
                  <td>{purge.operateur ?? 'Automatique'}</td>
                  <td>{purge.motif ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
